import { useCallback } from "react";
import Auth, { type DeliveryChannel } from "@Front/Api/Auth/Customer/Auth";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import type { CustomerLoginRouterScope } from "@Front/Components/Layouts/Login/helpers/customerLoginRouterScope";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import { extractHttpError } from "./loginErrorHandlers";
import type { ValidationError } from "class-validator";

export interface UseSendAnotherCodeOptions {
	email: string;
	totpCodeUid: string;
	loginScope: CustomerLoginRouterScope;
	authDisambiguation: CustomerAuthDisambiguation;
	setValidationErrors: (errors: ValidationError[]) => void;
	setPartialPhoneNumber: (value: string) => void;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	setMaskedEmail: (email: string) => void;
	setTotpCodeUid: (uid: string) => void;
}

function applySendAnotherCodeHttpError(
	error: unknown,
	email: string,
	totpCodeUid: string,
	setValidationErrors: UseSendAnotherCodeOptions["setValidationErrors"],
): void {
	const { http_status, message } = extractHttpError(error);
	LoggerService.error("sendAnotherCode failed", { error, email, totpCodeUid });
	setValidationErrors([
		{
			property: "totpCode",
			constraints: { [String(http_status ?? "unknown")]: message ?? "Une erreur est survenue." },
		},
	]);
}

async function runSendAnotherCodeFlow(opts: UseSendAnotherCodeOptions): Promise<void> {
	const { email, totpCodeUid, loginScope, authDisambiguation, setValidationErrors, setPartialPhoneNumber, setDeliveryChannel, setMaskedEmail, setTotpCodeUid } = opts;
	try {
		const res = await Auth.getInstance().sendAnotherCode({
			email,
			totpCodeUid,
			...buildCustomerLoginAuthScopePayload(
				loginScope.loginFolderUid,
				loginScope.loginOfficeUid,
				authDisambiguation,
				loginScope.loginCustomerUid,
			),
		});
		setValidationErrors([]);
		setPartialPhoneNumber(res.partialPhoneNumber ?? "");
		setDeliveryChannel(res.deliveryChannel);
		setMaskedEmail(res.maskedEmail ?? "");
		setTotpCodeUid(res.totpCodeUid);
	} catch (error: unknown) {
		applySendAnotherCodeHttpError(error, email, totpCodeUid, setValidationErrors);
	}
}

/**
 * Hook for send another code
 */
export function useSendAnotherCode(opts: UseSendAnotherCodeOptions): () => Promise<void> {
	return useCallback(async () => runSendAnotherCodeFlow(opts), [opts]);
}
